import { memo, useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { termsSchema } from "../schemas";

const CONTAINER_MIN_HEIGHT = 480;
const CONTAINER_STYLE = { minHeight: CONTAINER_MIN_HEIGHT };
const TERMS_SCROLL_STYLE = {
  maxHeight: "300px",
  overflowY: "auto" as const,
  fontSize: "0.9rem",
};

interface TermsData {
  isTermConditions: number;
  isDirectory: number;
}

interface TermsStepProps {
  defaultValues: TermsData;
  onNext: (data: TermsData) => void;
  onBack: () => void;
}

function TermsStepInner({
  defaultValues,
  onNext,
  onBack,
}: TermsStepProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TermsData>({
    resolver: yupResolver(termsSchema),
    defaultValues,
  });

  const termsScrollContent = useMemo(() => {
    const effectiveDate = new Date().toLocaleDateString();
    return (
      <div
        className="border rounded p-3 mb-2"
        style={TERMS_SCROLL_STYLE}
      >
        <strong>SMBF Membership Terms & Conditions</strong>
          <br />
          <em>Effective Date: {effectiveDate}</em>
          <br />
          <br />
          <strong>1. Renewals & Billing</strong>
          <br />
          Memberships are automatically renewed monthly or annually, based on
          your selected plan. You may update or cancel your payment method via
          your Member Dashboard.
          <br />
          <br />
          <strong>2. Cancellation by Member</strong>
          <br />
          Members may cancel the membership at any time by providing the
          required notice. To cancel, you must send us an email at
          Enquiries@smbf.global. Cancellation requests must be submitted at
          least 14 days before the next billing cycle.
          <br />
          <br />
          <strong>3. Cancellation by SMBF</strong>
          <br />
          We reserve the right to suspend or terminate your membership for
          misconduct or breach of terms. 24-hour written notice will be given.
          No refunds for terminations due to violations.
          <br />
          <br />
          <strong>4. Use of Personal Data</strong>
          <br />
          Your data is handled according to UK GDPR, EU GDPR, and global data
          privacy laws. We do not sell your data and implement strong security
          measures.
          <br />
          <br />
          <strong>5. Events</strong>
          <br />
          Event tickets are non-refundable unless cancelled by SMBF. Attendees
          must behave respectfully. SMBF may use photos or videos in promotions
          unless you opt out in writing.
          <br />
          <br />
          <strong>6. Products & Services</strong>
          <br />
          All offerings are provided "as is" without warranty. Refunds are only
          given if stated at the point of purchase.
          <br />
          <br />
          <strong>7. Member Conduct</strong>
          <br />
          Members must respect others, honour commitments, and avoid spam or
          aggressive marketing.
          <br />
          <br />
          <strong>8. Changes to Terms</strong>
          <br />
          We may update these terms at any time with notice.
          <br />
          <br />
          <strong>9. Liability</strong>
          <br />
          SMBF is not liable for losses from member interactions, events, or
          product usage.
          <br />
          <br />
          <strong>Member Charter</strong>
          <br />
          <br />
          <strong>SMBF Mission</strong>
          <br />
          ‘Supporting Muslim businesses, through ethical and meaningful
          collaboration, to positively impact the world.’
          <br />
          <br />
          <strong>The purpose of a code of conduct</strong>
          <br />
          All members of SMBF should embody noble character, morality, and
          ethical conduct (akhlaq) in their business activities in the Muslim
          community (ummah) and in dealings with other non-Muslim communities.
          <br />
          At the core of Islamic business ethics is a commitment to trust,
          honesty, and fair treatment of all stakeholders involved in business
          activities. We believe following Islamic ethics in our business
          activities is an act of worship and will produce positive business,
          personal and social outcomes.
          <br />
          This code of ethics is intended to enable SMBF to build a Muslim
          business network which builds trust and encourages cooperation,
          collaboration and community amongst Muslim business owners and
          professionals.
          <br />
          <br />
          <strong>Core Islamic ethical assumptions</strong>
          <br />
          The SMBF business ethics are based on core principles from Islam.
          <br />
          • God’s unity (Tawhid) – a person’s relationship with Allah, through
          the unity of the economic, religious, and social aspects of our life.
          <br />
          • Prophethood (Risalah): Upholding the ethical principles of business,
          trade, and moral conduct as outlined by the teachings of Final Prophet
          Muhammad (peace be upon Him).
          <br />
          • Justice and balance:
          <br />
          (‘Adl) – equity and balance, to behave justly to all. Do things in a
          proportionate manner and avoid extremes.
          <br />
          (Qist) – to give everyone and everything their proper due, share,
          portion, measure, allotment, or amount. A person should be just in
          every aspect of their life to all people, things and at all times.
          <br />
          • Trusteeship:
          <br />
          (khilafah), people are entrusted with the resources given to them by
          Allah.
          <br />
          (Amanah) the wealth and resources that mankind has access to are not
          owned but are in trust from Allah and we are responsible for their
          stewardship, care, and use.
          <br />
          <br />
          <em>¹ Surah Al-Ikhlas (112:1-4)</em>
          <br />
          <em>² Surah Al Maidah (5:8)</em>
          <br />
          <em>Surah Al-An’am (6:152)</em>
          <br />
          <em>Surah Al-Baqarah (2:282)</em>
          <br />
          <em>³ Surah Al-An’am (6:152)</em>
          <br />
          <em>Surah Al-A’raf (7:29)</em>
          <br />
          <em>Surah Ar-Rahman (55:09)</em>
          <br />
          <em>Surah Al-Isra (17:35)</em>
          <br />
          <em>Surah Ash Shu’ara (26:182)</em>
          <br />
          <em>⁴ Surah Al-Baqarah (2:30)</em>
          <br />
          <em>⁵ Surah Al-Ahzab (33:72)</em>
          <br />
          <br />
          • Moderation (wasat or I’tidal) – eliminating excess and deficiencies
          and seeking a sense of balance. Maintaining the balance between the
          needs of the body, mind and soul while providing everyone and
          everything their due.
          <br />
          • Benevolence (ihsaan) kindness to others – is an act which benefits
          persons others than those for whom the act proceeds without
          obligation. Conducting business activities and work to the highest
          standards to the best of your abilities for the love of Allah.
          <br />
          • Free will – a person’s ability to act without external coercion and
          to be responsible for their actions within the parameters of Allah’s
          creation and as Allah’s trustee on earth.
          <br />
          <br />
          <strong>Contracts and Agreements</strong>
          <br />
          In line with Qur’anic instruction, all business agreements, especially
          between members, must be written, signed, and where appropriate,
          witnessed. Allah Almighty says in the Noble Qur'an: “O you who
          believe! When you contract a debt for a fixed period, write it down…”
          (Surah Al-Baqarah, 2:282). This principle extends to all commercial
          dealings and ensures clarity, fairness, and accountability in line
          with Islamic ethics.
          <br />
          <br />
          <strong>
            As a member of SMBF I am committed to the following code of business
            ethics in my business and professional activities:
          </strong>
          <br />
          <strong>My customers and the clients I serve:</strong>
          <br />
          • I will work to improve my costs of operation in order to charge
          reasonable prices.
          <br />
          • Products and services will be delivered in a timely and efficient
          manner without errors to the best of my ability.
          <br />
          • I will follow the tradition of the Final Prophet Muhammad (peace be
          upon Him) in being truthful and transparent when describing my
          products and services to customers or clients.
          <br />
          • I will not deny products or services to any customers on the basis
          of race, religion, gender, or national origin.
          <br />
          <br />
          <strong>My suppliers and distributors:</strong>
          <br />
          • I will source good quality suppliers, distributors, manufacturers,
          or business-to-business services to ensure I meet the needs of my
          customers in the best manner possible.
          <br />
          • I will ensure my suppliers and distributors make a fair profit.
          <br />
          • I shall neither offer nor accept any bribe, or other un-Islamic
          inducement in transactions with suppliers and distributors or any
          other stakeholders.
          <br />
          • I will be transparent and honest in dealings with suppliers and
          honour the fulfilment of contracts, commitment, and promises, in
          alignment with my principles as a Muslim.
          <br />
          • My business commitments and obligations will be in a written
          contract agreed by all parties in accordance with Islamic principles.
          <br />
          <br />
          <strong>My employees, workers, and volunteers:</strong>
          <br />
          • I will ensure every employee, worker or volunteer will work in safe
          and clean conditions.
          <br />
          • I will ensure my employees and workers will receive fair and
          adequate salary, wages and benefits paid in a timely manner and in
          accordance with their contracts.
          <br />
          • I will ensure volunteers will receive fair, adequate, and timely
          reimbursement of legitimate expenses incurred in voluntary activities
          for my organisation.
          <br />
          • I will ensure employees, workers and volunteers will have ample
          opportunities to develop their education and skills.
          <br />
          • I will ensure employees will be free to make suggestions,
          criticisms, or complaints without fear of retribution or
          victimisation.
          <br />
          • I will safeguard employees, workers, and volunteers’ rights to
          privacy, protect them from any form of bullying or harassment and
          respect their dignity at all times.
          <br />
          • I will ensure the company, directors and managers will clearly
          communicate to all employees, workers or volunteers what is expected
          of them in carrying out their work and duties.
          <br />
          • I will conduct all consultation and negotiations with employees,
          workers, and volunteers in good faith and with integrity.
          <br />
          • I will encourage every employee, worker, and volunteer to take
          personal responsibility to ensure their actions agree with Islamic
          values and the Code of Ethics of the company.
          <br />
          • I will expect and encourage my employees to honour their obligations
          to work diligently and protect the secrecy, interests, resources, and
          privacy of the business.
          <br />
          <br />
          <strong>My competitors:</strong>
          <br />
          • I will not engage in monopolistic or unethical behaviour which
          precludes others from competing with my business, organisation or
          accessing employment opportunities.
          <br />
          • I will compete fairly without engaging in un-Islamic tactics, such
          as bribery, fraud, deception, hoarding, price manipulation, trading in
          uncertainty and products I do not possess.
          <br />
          <br />
          <strong>My stockholders and investors:</strong>
          <br />
          • I will work to the best of my ability to ensure a fair return to my
          stockholders and investors, by following the principles of Islamic
          business ethics.
          <br />
          • I will only engage in business activities which benefits others and
          society (halal) and will not engage in harmful business activities
          (haram) such as the production and sale of alcohol, prostitution,
          gambling, trading in stolen goods, interest on capital (riba), or any
          other activities which go against Islam.
          <br />
          • I will use investor and stockholders’ funds in a cost-efficient
          manner in all activities when developing the business, including
          research and development in products, services, or the expansion of
          the operations of the company.
          <br />
          • I will compensate our employees and workers equitably and offer them
          the option of purchasing a stake in the business in accordance with
          Islamic principles, which will not negatively affect their salary or
          benefits.
          <br />
          • I will maintain appropriate financial reserves and resources in the
          business to enable the operation of the business in difficult times.
          <br />
          • I will exercise careful judgement over the use of company finances
          and resources and avoid unnecessary, wasteful, or excessive
          expenditure to ensure I provide an Islamically acceptable rate of
          return to my shareholders and investors.
          <br />
          <br />
          <strong>Our community (Ummah):</strong>
          <br />
          • I will support the community in which I live as well as the whole
          ummah, in an ethical, professional, and personal capacity where
          possible.
          <br />
          • I will be a good citizen, paying my fair share of taxes and
          contributing to the welfare of the needy and destitute.
          <br />
          • I will follow national and international law in all my personal and
          business activities.
          <br />
          • I will pay my zakat annually.
          <br />
          • I will protect and enhance the environment and natural resources, in
          my capacity as a Khalifa on the earth, and ensure my use of the
          environment and natural resources for business and professional
          activities is sustainable.
          <br />
          • I will treat all communities equally and fairly and not discriminate
          on the basis of race, religion, nationality, ethnicity, disability, or
          gender according to Islamic principles.
          <br />
          <br />
          Created by SMBF Member Geoff Boot
          <br />
          Reviewed by Istiana Board
          <br />
          <br />
          <em>23 Surah Al-Baqarah (2:267)</em>
          <br />
          <em>24 Surah Al-Baqarah (2:219)</em>
          <br />
          <em>25 Surah Al-Isra (17:32)</em>
          <br />
          <em>26 Surah Al-Baqarah (2:219)</em>
          <br />
          <em>27 Sahih Muslim: Book 1, Hadith 190</em>
          <br />
          <em>28 Surah Al-Baqarah (2:275)</em>
          <br />
          <em>29 Surah Al-Baqarah (2:195)</em>
          <br />
          <em>30 Sahih Al-Bukhari: Volume 2, Book 24, Number 484</em>
          <br />
          <br />
          By registering, you confirm you have read and agree to these Terms &
          Conditions.
        </div>
    );
  }, []);

  const onSubmit = useCallback(
    (data: TermsData) => {
      onNext(data);
    },
    [onNext]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <div
        className="d-flex flex-column gap-3"
        style={CONTAINER_STYLE}
      >
        <h5>Terms & Conditions and Member Charter.</h5>
        {termsScrollContent}
        <div className="d-flex flex-column px-3">
          <div>
            <Controller
              name="isTermConditions"
              control={control}
              render={({ field }) => (
                <div className="d-flex align-items-baseline">
                  <input
                    type="checkbox"
                    id="terms2"
                    {...field}
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                  <label htmlFor="terms2" className="ms-2">
                    I agree to the above Terms and Conditions and to abide by
                    the Member Charter
                  </label>
                </div>
              )}
            />
            {errors.isTermConditions && (
              <p className="custom-error-text mb-0 px-1">
                {errors.isTermConditions.message}
              </p>
            )}
          </div>
          <div className="mb-2">
            <Controller
              name="isDirectory"
              control={control}
              render={({ field }) => (
                <div className="d-flex align-items-baseline">
                  <input
                    type="checkbox"
                    id="terms1"
                    {...field}
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                  <label htmlFor="terms1" className="ms-2">
                    Cancellation requests may only be submitted following
                    completion of this initial term and must be provided in
                    writing to enquiries@smbf.global no later than 14 days
                    before the next billing date.
                  </label>
                </div>
              )}
            />
            {errors.isDirectory && (
              <p className="custom-error-text mb-0 px-1">
                {errors.isDirectory.message}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="submit"
          className="btn btn-primary"
        >
          Next
        </button>
      </div>
    </form>
  );
}

TermsStepInner.displayName = "TermsStep";

const TermsStep = memo(TermsStepInner);
TermsStep.displayName = "TermsStep";

export default TermsStep;
